import { useState, useMemo, useEffect, useCallback } from 'react';
import { CalendarCheck, History } from 'lucide-react';
import WeeklyReviewWizard from '../components/weekly-review/WeeklyReviewWizard';
import InboxProcessor from '../components/planning/InboxProcessor';
import MindSweep from '../components/weekly-review/MindSweep';
import WeekInReview from '../components/weekly-review/WeekInReview';
import ProjectReview from '../components/weekly-review/ProjectReview';
import WaitingForReview from '../components/weekly-review/WaitingForReview';
import CalendarReview from '../components/weekly-review/CalendarReview';
import SomedayReview from '../components/weekly-review/SomedayReview';
import ReviewComplete from '../components/weekly-review/ReviewComplete';
import WeeklyReviewHistoryPage from './WeeklyReviewHistoryPage';
import ProjectModal from '../components/ProjectModal';
import GoalModal from '../components/GoalModal';
import LinkModal from '../components/LinkModal';
import OneOnOnePickerModal from '../components/OneOnOnePickerModal';
import TaskModal from '../components/TaskModal';
import { useTasks } from '../hooks/useTasks';
import { useProjects } from '../hooks/useProjects';
import { useGoals } from '../hooks/useGoals';
import { useLinks } from '../hooks/useLinks';
import { useCheckoffs } from '../hooks/useCheckoffs';
import { useOneOnOneRelationships } from '../hooks/useOneOnOneRelationships';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { useWeeklyReview } from '../hooks/useWeeklyReview';
import { useAuth } from '../hooks/useAuth';
import { getWeekBounds, getWeekString, getLocalDateString } from '../utils/dateUtils';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import FeatureOnboarding from '../components/common/FeatureOnboarding';

function getInternalUserName(displayName) {
  if (!displayName) return null;
  const firstName = displayName.split(' ')[0];
  if (firstName === 'Jason') return 'Jason';
  if (firstName === 'Matt' || firstName === 'Matthew') return 'Matt';
  return firstName;
}

export default function WeeklyReviewPage() {
  const { user } = useAuth();
  const currentUserName = getInternalUserName(user?.displayName);

  const [currentStep, setCurrentStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [processedInboxCount, setProcessedInboxCount] = useState(0);
  const [initialInboxCount, setInitialInboxCount] = useState(0);
  const [reviewStartTime, setReviewStartTime] = useState(null);

  // Track reviewed items
  const [projectsReviewed, setProjectsReviewed] = useState(new Set());
  const [waitingForReviewed, setWaitingForReviewed] = useState(new Set());

  // Reflection state
  const [reflection, setReflection] = useState({
    wentWell: '',
    couldImprove: '',
    nextWeekFocus: ''
  });

  // Quick action modal states
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showOneOnOnePickerModal, setShowOneOnOnePickerModal] = useState(false);
  const [currentInboxItem, setCurrentInboxItem] = useState(null);
  const [prefillData, setPrefillData] = useState(null);

  // Get current week bounds (Monday-Sunday)
  const weekBounds = useMemo(() => getWeekBounds(new Date()), []);
  const currentWeekString = useMemo(() => getWeekString(new Date()), []);

  // Start timer on mount
  useEffect(() => {
    setReviewStartTime(new Date());
  }, []);

  // Task management
  const {
    tasks,
    getInboxTasks,
    addToToday,
    moveToWaiting,
    moveToSomeday,
    scheduleTask,
    deleteTask,
    addTask,
    updateTask
  } = useTasks(currentUserName);

  // Projects, goals, links
  const { projects, addProject, updateProject } = useProjects(currentUserName);
  const { goals, addGoal } = useGoals();
  const { links, addLink } = useLinks();
  const { isChecked } = useCheckoffs();

  // Weekly review persistence
  const { saveWeeklyReview, getReviewForWeek } = useWeeklyReview();

  // Google Calendar
  const {
    isSignedIn: gcalSignedIn,
    getAllEventsForDate,
    TIME_BLOCK_NAMES
  } = useGoogleCalendar();

  // 1:1 Relationships
  const { activeRelationships } = useOneOnOneRelationships();

  // Get current inbox items
  const inboxItems = useMemo(() => getInboxTasks(), [getInboxTasks, tasks]);

  // Set initial inbox count on first render
  useEffect(() => {
    if (initialInboxCount === 0 && inboxItems.length > 0) {
      setInitialInboxCount(inboxItems.length);
    }
  }, [inboxItems.length, initialInboxCount]);

  // Calculate week metrics
  const weekMetrics = useMemo(() => {
    const weekStart = weekBounds.start;
    const weekEnd = weekBounds.end;

    // Tasks completed this week
    const tasksCompletedThisWeek = tasks.filter(t => {
      if (!t.completedAt) return false;
      const completedDate = t.completedAt.toDate ? t.completedAt.toDate() : new Date(t.completedAt);
      return completedDate >= weekStart && completedDate <= weekEnd;
    });

    // Tasks carried over (scheduled for this week but not completed)
    const tasksCarriedOver = tasks.filter(t => {
      if (t.completedAt) return false;
      if (!t.scheduledDate) return false;
      const scheduledDate = new Date(t.scheduledDate + 'T00:00:00');
      return scheduledDate >= weekStart && scheduledDate <= weekEnd;
    });

    // Waiting for items
    const waitingForItems = tasks.filter(t => t.waitingFor && !t.completedAt);

    // Someday items
    const somedayItems = tasks.filter(t => t.isSomeday && !t.completedAt);

    // Active projects
    const activeProjects = projects.filter(p => p.status === 'active');

    // Projects without next actions
    const projectsWithoutNextActions = activeProjects.filter(project => {
      const projectTasks = tasks.filter(t =>
        t.projectId === project.id &&
        !t.completedAt &&
        !t.isSomeday
      );
      return projectTasks.length === 0;
    });

    // Habits completion rate for the week
    const weeklyHabits = links.filter(l => {
      const assignments = l.assignments || {};
      return Object.values(assignments).includes('weekly');
    });
    const weeklyHabitsChecked = weeklyHabits.filter(l => isChecked(l.id, 'weekly')).length;
    const habitsCompletionRate = weeklyHabits.length > 0
      ? Math.round((weeklyHabitsChecked / weeklyHabits.length) * 100)
      : 100;

    return {
      tasksCompletedCount: tasksCompletedThisWeek.length,
      tasksCompletedList: tasksCompletedThisWeek,
      tasksCarriedOverCount: tasksCarriedOver.length,
      tasksCarriedOverList: tasksCarriedOver,
      waitingForItems,
      somedayItems,
      activeProjects,
      projectsWithoutNextActions,
      habitsCompletionRate,
      weeklyHabitsTotal: weeklyHabits.length,
      weeklyHabitsChecked
    };
  }, [tasks, projects, links, weekBounds, isChecked]);

  // Inbox processing handlers
  const handleAddToToday = async (taskId) => {
    await addToToday(taskId);
    setProcessedInboxCount(prev => prev + 1);
  };

  const handleMoveToWaiting = async (taskId) => {
    await moveToWaiting(taskId);
    setProcessedInboxCount(prev => prev + 1);
  };

  const handleMoveToSomeday = async (taskId) => {
    await moveToSomeday(taskId);
    setProcessedInboxCount(prev => prev + 1);
  };

  const handleSchedule = async (taskId, date) => {
    await scheduleTask(taskId, date);
    setProcessedInboxCount(prev => prev + 1);
  };

  const handleDelete = async (taskId) => {
    await deleteTask(taskId);
    setProcessedInboxCount(prev => prev + 1);
  };

  // Quick action handlers for inbox
  const handleCreateProject = (item) => {
    setCurrentInboxItem(item);
    setPrefillData({
      name: item.title,
      description: item.notes || '',
      source: item.source,
      sourceChannel: item.sourceChannel,
      sourceLink: item.sourceLink
    });
    setShowProjectModal(true);
  };

  const handleSaveProject = async (projectData) => {
    await addProject(projectData);
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
      setProcessedInboxCount(prev => prev + 1);
    }
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  const handleAddAsGoal = (item) => {
    setCurrentInboxItem(item);
    setPrefillData({
      title: item.title,
      description: item.notes || ''
    });
    setShowGoalModal(true);
  };

  const handleSaveGoal = async (goalData) => {
    await addGoal(goalData);
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
      setProcessedInboxCount(prev => prev + 1);
    }
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  const handleAddAsHabit = (item) => {
    setCurrentInboxItem(item);
    setPrefillData({
      name: item.title,
      url: item.sourceLink || ''
    });
    setShowLinkModal(true);
  };

  const handleSaveHabit = async (linkData) => {
    await addLink(linkData);
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
      setProcessedInboxCount(prev => prev + 1);
    }
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  const handleAddToOneOnOne = (item) => {
    setCurrentInboxItem(item);
    setShowOneOnOnePickerModal(true);
  };

  const handleOneOnOneSelect = async ({ relationship, type }) => {
    // Similar to EODPlanningPage implementation
    setCurrentInboxItem(null);
    setShowOneOnOnePickerModal(false);
  };

  // Mind sweep handlers
  const handleQuickAddTask = () => {
    setPrefillData(null);
    setShowTaskModal(true);
  };

  const handleSaveTask = async (taskData) => {
    await addTask(taskData);
    setShowTaskModal(false);
    setPrefillData(null);
  };

  // Project review handlers
  const handleMarkProjectReviewed = (projectId) => {
    setProjectsReviewed(prev => new Set([...prev, projectId]));
  };

  const handleAddNextAction = async (projectId, taskTitle) => {
    await addTask({
      title: taskTitle,
      projectId,
      status: 'next'
    });
  };

  // Waiting for review handlers
  const handleMarkWaitingReviewed = (taskId) => {
    setWaitingForReviewed(prev => new Set([...prev, taskId]));
  };

  const handleFollowUp = async (taskId) => {
    // Convert waiting task to active task for follow-up
    await updateTask(taskId, { waitingFor: null });
    handleMarkWaitingReviewed(taskId);
  };

  // Handle finish
  const handleFinish = async () => {
    const endTime = new Date();
    const durationMinutes = reviewStartTime
      ? Math.round((endTime - reviewStartTime) / 1000 / 60)
      : 0;

    // Save the weekly review
    await saveWeeklyReview({
      week: currentWeekString,
      metrics: {
        tasksCompletedCount: weekMetrics.tasksCompletedCount,
        tasksCarriedOverCount: weekMetrics.tasksCarriedOverCount,
        inboxProcessedCount: processedInboxCount,
        projectsReviewedCount: projectsReviewed.size,
        projectsWithNoNextAction: weekMetrics.projectsWithoutNextActions.length,
        waitingForCount: weekMetrics.waitingForItems.length,
        habitsCompletionRate: weekMetrics.habitsCompletionRate,
        oneOnOnesCompleted: 0, // TODO: Calculate from 1:1 data
        timeBlocksCount: 0 // TODO: Calculate from calendar data
      },
      reflection: reflection.wentWell || reflection.couldImprove || reflection.nextWeekFocus
        ? reflection
        : null,
      durationMinutes
    });

    setIsComplete(true);
  };

  // Handle done (close/reset)
  const handleDone = () => {
    setCurrentStep(1);
    setIsComplete(false);
    setProcessedInboxCount(0);
    setInitialInboxCount(0);
    setProjectsReviewed(new Set());
    setWaitingForReviewed(new Set());
    setReflection({ wentWell: '', couldImprove: '', nextWeekFocus: '' });
    setReviewStartTime(new Date());
  };

  // Calculate completion stats
  const completionStats = useMemo(() => ({
    inboxProcessed: processedInboxCount,
    tasksCompleted: weekMetrics.tasksCompletedCount,
    projectsReviewed: projectsReviewed.size,
    totalProjects: weekMetrics.activeProjects.length,
    habitsRate: weekMetrics.habitsCompletionRate,
    waitingForReviewed: waitingForReviewed.size,
    totalWaitingFor: weekMetrics.waitingForItems.length
  }), [processedInboxCount, weekMetrics, projectsReviewed.size, waitingForReviewed.size]);

  // Show history page
  if (showHistory) {
    return (
      <div className="h-full flex flex-col bg-surface-base">
        <WeeklyReviewHistoryPage onBack={() => setShowHistory(false)} />
      </div>
    );
  }

  // Show completion screen
  if (isComplete) {
    return (
      <div className="h-full flex flex-col bg-surface-base">
        <ReviewComplete
          stats={completionStats}
          week={currentWeekString}
          reflection={reflection}
          onDone={handleDone}
        />
      </div>
    );
  }

  return (
    <FeatureOnboarding feature="weeklyReview">
    <div className="h-full flex flex-col bg-surface-base">
      {/* Wizard navigation header */}
      <WeeklyReviewWizard
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        onFinish={handleFinish}
        onShowHistory={() => setShowHistory(true)}
        canAdvance={true}
        inboxCount={inboxItems.length}
        processedCount={processedInboxCount}
      />

      {/* Step content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Step 1: Process Inbox */}
        {currentStep === 1 && (
          <InboxProcessor
            inboxItems={inboxItems}
            onAddToToday={handleAddToToday}
            onMoveToWaiting={handleMoveToWaiting}
            onMoveToSomeday={handleMoveToSomeday}
            onSchedule={handleSchedule}
            onDelete={handleDelete}
            onCreateProject={handleCreateProject}
            onAddToOneOnOne={handleAddToOneOnOne}
            onAddAsGoal={handleAddAsGoal}
            onAddAsHabit={handleAddAsHabit}
            processedCount={processedInboxCount}
          />
        )}

        {/* Step 2: Mind Sweep */}
        {currentStep === 2 && (
          <MindSweep
            onAddTask={handleQuickAddTask}
            onAddProject={() => {
              setPrefillData(null);
              setShowProjectModal(true);
            }}
            onAddGoal={() => {
              setPrefillData(null);
              setShowGoalModal(true);
            }}
          />
        )}

        {/* Step 3: Week in Review */}
        {currentStep === 3 && (
          <WeekInReview
            metrics={weekMetrics}
            weekBounds={weekBounds}
          />
        )}

        {/* Step 4: Project Review */}
        {currentStep === 4 && (
          <ProjectReview
            projects={weekMetrics.activeProjects}
            projectsWithoutNextActions={weekMetrics.projectsWithoutNextActions}
            reviewedProjects={projectsReviewed}
            onMarkReviewed={handleMarkProjectReviewed}
            onAddNextAction={handleAddNextAction}
            onUpdateProject={updateProject}
          />
        )}

        {/* Step 5: Waiting For Review */}
        {currentStep === 5 && (
          <WaitingForReview
            waitingItems={weekMetrics.waitingForItems}
            reviewedItems={waitingForReviewed}
            onMarkReviewed={handleMarkWaitingReviewed}
            onFollowUp={handleFollowUp}
            onComplete={async (taskId) => {
              await updateTask(taskId, { completedAt: new Date() });
              handleMarkWaitingReviewed(taskId);
            }}
          />
        )}

        {/* Step 6: Calendar Review */}
        {currentStep === 6 && (
          <CalendarReview
            weekBounds={weekBounds}
            gcalSignedIn={gcalSignedIn}
            getAllEventsForDate={getAllEventsForDate}
          />
        )}

        {/* Step 7: Someday/Goals + Reflection */}
        {currentStep === 7 && (
          <SomedayReview
            somedayItems={weekMetrics.somedayItems}
            goals={goals}
            reflection={reflection}
            onReflectionChange={setReflection}
            onActivateSomeday={async (taskId) => {
              await updateTask(taskId, { isSomeday: false });
            }}
            onDeleteSomeday={deleteTask}
          />
        )}
      </div>

      {/* Quick action modals */}
      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => {
          setShowProjectModal(false);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveProject}
        editItem={prefillData}
      />

      <GoalModal
        isOpen={showGoalModal}
        onClose={() => {
          setShowGoalModal(false);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveGoal}
        editItem={prefillData}
      />

      <LinkModal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveHabit}
        editItem={prefillData}
      />

      <TaskModal
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setPrefillData(null);
        }}
        onSave={handleSaveTask}
      />

      <OneOnOnePickerModal
        isOpen={showOneOnOnePickerModal}
        onClose={() => {
          setShowOneOnOnePickerModal(false);
          setCurrentInboxItem(null);
        }}
        onSelect={handleOneOnOneSelect}
        itemTitle={currentInboxItem?.title}
      />
    </div>
    </FeatureOnboarding>
  );
}
